/**
 * The header and the footer, as parts of the document you can put a caret in.
 *
 * GOOGLE DOCS PUTS THE FOOTER IN THE DOCUMENT, and that is the right model here too. A footer
 * drawn from the record (DocumentFooter.tsx) is correct and is never wrong -- but it is also never
 * the author's. A controlled document needs both: identity that cannot go stale, and a footer the
 * document's owner can actually write.
 *
 * SO IT IS A NODE AT THE END OF THE DOC, not a second editor and not a column on the row.
 * Everything that already works keeps working for free, and none of it had to be re-implemented:
 *
 *   - the CRDT carries it, so two people editing see each other's footer change;
 *   - undo/redo covers it, because it is one of the same transactions;
 *   - the debounced save stores it, because it IS the body;
 *   - the version history has it, because a version is a body;
 *   - @media print already pins `.parkour-footer` to the bottom of every page.
 *
 * A separate editable region -- which is what a word processor really has -- would have needed a
 * second Yjs document, a second save path, a second digest, and a second place for a wipe to
 * happen. The node is the same feature for none of that.
 *
 * THE FACTS STAY FACTS. The document number, the revision and the state are FIELDS (docField), not
 * typed text: atoms that render whatever the record says right now. That is the whole reason the
 * old footer refused to be editable, and it is kept -- an author can move them, delete them,
 * write around them, and cannot freeze last year's revision into a procedure by typing it.
 *
 * ONE FOOTER, AT THE END. docFooter is deliberately NOT in the `block` group, and the document's
 * content expression admits at most one as the last child, so there is no paste, drag or command
 * that can leave a footer in the middle of chapter three.
 */
import { Node, mergeAttributes } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";
import { DocAttrStep } from "@tiptap/pm/transform";

import { PAGE_GAP, pageGeometry } from "./pageGeometry";

// Re-exported so the components that already import them from here keep working;
// pageGeometry.ts is where they are defined and tested.
export { PAGE_GAP, pageGeometry };

/** The facts a field can show. The keys are stored IN THE DOCUMENT, so they do not change. */
export type DocumentFieldName = "objectId" | "revision" | "status" | "account" | "name";

export const FIELD_LABELS: Record<DocumentFieldName, string> = {
    objectId: "Document number",
    name: "Document name",
    revision: "Revision",
    status: "State",
    account: "Account name",
};

export type DocumentFieldValues = Partial<Record<DocumentFieldName, string | null>>;

// THE CURRENT VALUES, MODULE-LEVEL, with listeners -- because a node view is a piece of DOM the
// editor owns and not a React component, and threading context into one would mean a React node
// view, a renderer and a provider for four strings that change once per document.
let values: DocumentFieldValues = {};
const listeners = new Set<() => void>();

export function setDocumentFieldValues(next: DocumentFieldValues) {
    values = { ...next };
    listeners.forEach((redraw) => redraw());
}

/**
 * What a field reads on the page.
 *
 * THE NAME IN BRACES WHEN THERE IS NO VALUE, not an empty span. A released document has no state
 * to print and a document still loading has no number; a footer that silently lost a word reads as
 * a bug, and `{Revision}` tells the author exactly what will appear there.
 */
export function fieldText(field: DocumentFieldName): string {
    const value = values[field];
    if (value) return String(value);
    return `{${FIELD_LABELS[field]}}`;
}

export const DocField = Node.create({
    name: "docField",
    group: "inline",
    inline: true,
    atom: true,
    // NOT SELECTABLE, AND THAT IS NOT A PREFERENCE. ProseMirror's Selection.near prefers a
    // NodeSelection on a selectable node, so a caret aimed at the start of the footer's first
    // paragraph -- which begins with a field -- landed ON the field instead of before it, and the
    // first thing typed replaced the document number. Backspace and Delete still remove a field,
    // which is the only thing selecting one was for.
    //
    // NOT DRAGGABLE either: dragging an atom out of a table cell lands it wherever the drop cursor
    // was, which in a three-cell footer is almost never where it was aimed.
    selectable: false,
    draggable: false,

    addAttributes() {
        return { field: { default: "objectId" as DocumentFieldName } };
    },

    parseHTML() {
        return [{
            tag: "span[data-parkour-field]",
            getAttrs: (element) => ({ field: (element as HTMLElement).getAttribute("data-parkour-field") }),
        }];
    },

    /**
     * The STATIC rendering -- the clipboard, and anything that serialises the document to HTML.
     *
     * It writes the value as text AND keeps the field name in the attribute, so copying a footer
     * into another document carries a live field rather than a frozen string, while pasting it into
     * mail carries something readable.
     */
    renderHTML({ HTMLAttributes, node }) {
        const field = (node.attrs.field || "objectId") as DocumentFieldName;
        return ["span", mergeAttributes(HTMLAttributes, {
            "data-parkour-field": field, class: "parkour-field",
        }), fieldText(field)];
    },

    renderText({ node }) {
        return fieldText((node.attrs.field || "objectId") as DocumentFieldName);
    },

    addNodeView() {
        return ({ node }) => {
            const dom = document.createElement("span");
            dom.className = "parkour-field";
            dom.setAttribute("data-parkour-field", String(node.attrs.field));
            // NOT EDITABLE: the caret must step over a field, not into it. Typing inside one would
            // produce text that looks like a field and is not.
            dom.contentEditable = "false";
            const draw = () => {
                dom.textContent = fieldText((node.attrs.field || "objectId") as DocumentFieldName);
            };
            draw();
            listeners.add(draw);
            return { dom, destroy: () => { listeners.delete(draw); } };
        };
    },
});

/**
 * The two page elements, which are the same node with two names.
 *
 * SEPARATE TYPES RATHER THAN ONE WITH AN ATTRIBUTE, because the document's content expression is
 * what guarantees there is at most one of each and that they are at the ends -- `docHeader? block+
 * docFooter?` says it in the schema, where no command, paste or drag can get round it. One shared
 * type with a `where` attribute would need every one of those paths to check it by hand.
 */
const pageElement = (name: string, className: string) => Node.create({
    name,
    // NO `group`, so nothing that accepts a block accepts this one.
    content: "block+",
    // ISOLATING, so Backspace at the start of the footer does not merge it into the last paragraph
    // of the body, and a selection dragged out of it does not take the body with it.
    isolating: true,
    defining: true,
    selectable: false,

    parseHTML() {
        return [{ tag: `div[data-parkour-${name === "docHeader" ? "header" : "footer"}]` }];
    },

    renderHTML({ HTMLAttributes }) {
        // THE CLASS THE PRINT STYLESHEET KEYS ON. See @media print in index.css: these two are
        // what get pinned to the top and bottom of every printed page.
        return ["div", mergeAttributes(HTMLAttributes, {
            [`data-parkour-${name === "docHeader" ? "header" : "footer"}`]: "true", class: className,
        }), 0];
    },
});

export const DocHeader = pageElement("docHeader", "parkour-header");
export const DocFooter = pageElement("docFooter", "parkour-footer");

/** The document's own content rule: at most one header, the body, at most one footer. */
export const DOCUMENT_CONTENT = "docHeader? block+ docFooter?";

const cell = (align: "left" | "center" | "right", content: any[]) => ({
    type: "tableCell",
    content: [{ type: "paragraph", attrs: { textAlign: align }, content }],
});

const field = (name: DocumentFieldName) => ({ type: "docField", attrs: { field: name } });

/**
 * What a new footer says before anybody edits it.
 *
 * THE HOUSE FOOTER, seeded rather than imposed -- the same three cells the automatic footer drew,
 * now as the author's own text. Inserting a footer must not be a step DOWN from having none, and
 * an empty line would be exactly that.
 *
 * A ONE-ROW TABLE IS HOW LEFT/CENTRE/RIGHT IS DONE. A paragraph has one alignment, so the three
 * markings cannot share a line in one; this editor has no tab stops (and a tab stop that drifts
 * with the typeface is worse than none). The cells are borderless on screen and in print.
 */
export const defaultFooter = () => ({
    type: "docFooter",
    content: [{
        type: "table",
        content: [{
            type: "tableRow",
            content: [
                cell("left", [field("objectId"), { type: "text", text: " Rev. " }, field("revision")]),
                cell("center", [field("account"), { type: "text", text: " Confidential" }]),
                cell("right", [field("status")]),
            ],
        }],
    }],
});

/**
 * What a new header says before anybody edits it.
 *
 * TITLE LEFT, NUMBER AND REVISION RIGHT -- the arrangement nearly every controlled document uses,
 * and the one that answers the two questions a reader has when a page arrives on its own: what is
 * this, and which version. The confidentiality marking stays in the footer so the two do not say
 * the same thing twice.
 */
export const defaultHeader = () => ({
    type: "docHeader",
    content: [{
        type: "table",
        content: [{
            type: "tableRow",
            content: [
                cell("left", [field("name")]),
                cell("right", [field("objectId"), { type: "text", text: " Rev. " }, field("revision")]),
            ],
        }],
    }],
});

/**
 * The first place inside `from`..`to` where a keystroke means something.
 *
 * NOT ARITHMETIC ON THE INSERT POSITION. "Four past the start" is the paragraph inside the cell
 * inside the row inside the table -- until a footer holds a plain paragraph instead of a table, and
 * then it is past the end of it. Counting node boundaries by hand is how a caret ends up at a
 * block boundary, where ProseMirror answers a keystroke by opening a new empty paragraph: typing
 * twenty-seven characters into a brand new footer produced twenty-seven empty lines above it.
 */
function firstTextPosition(doc: any, from: number, to: number): number | null {
    let found: number | null = null;
    doc.nodesBetween(from, to, (node: any, pos: number) => {
        if (found === null && node.isTextblock) found = pos + 1;
    });
    return found;
}

/**
 * Where a page element is, or null.
 *
 * EACH CAN ONLY BE AT ITS OWN END -- the schema says so -- so this is a look at one child rather
 * than a walk, and a header found anywhere else would be a document this build did not write.
 */
function elementAt(editor: any, name: "docHeader" | "docFooter"): number | null {
    const doc = editor?.state?.doc;
    if (!doc) return null;
    if (name === "docHeader") {
        return doc.firstChild?.type.name === "docHeader" ? 0 : null;
    }
    const last = doc.lastChild;
    return last?.type.name === "docFooter" ? doc.content.size - last.nodeSize : null;
}

export const hasHeader = (editor: any) => elementAt(editor, "docHeader") !== null;
export const hasFooter = (editor: any) => elementAt(editor, "docFooter") !== null;

/**
 * Insert a header or a footer, or put the caret in the one that is there.
 *
 * ONE MENU ITEM FOR BOTH JOBS, as in every word processor: "Footer" means "take me to the footer",
 * and whether it had to be created first is not a question the author should have to answer.
 */
function insertElement(editor: any, name: "docHeader" | "docFooter", make: () => any) {
    if (!editor) return;
    const existing = elementAt(editor, name);
    const { state, view } = editor;

    if (existing !== null) {
        const node = name === "docHeader" ? state.doc.firstChild : state.doc.lastChild;
        const at = firstTextPosition(state.doc, existing, existing + node.nodeSize);
        if (at !== null) {
            view.dispatch(state.tr.setSelection(TextSelection.create(state.doc, at)).scrollIntoView());
        }
        view.focus();
        return;
    }

    const where = name === "docHeader" ? 0 : state.doc.content.size;
    const tr = state.tr.insert(where, state.schema.nodeFromJSON(make()));
    // INTO THE FIRST CELL, where a keystroke is a character -- found rather than counted; see
    // firstTextPosition for what counting cost.
    const inserted = tr.doc.nodeAt(where);
    const at = firstTextPosition(tr.doc, where, where + (inserted?.nodeSize ?? 0));
    if (at !== null) tr.setSelection(TextSelection.create(tr.doc, at));
    view.dispatch(tr.scrollIntoView());
    view.focus();
}

export const insertHeader = (editor: any) => insertElement(editor, "docHeader", defaultHeader);
export const insertFooter = (editor: any) => insertElement(editor, "docFooter", defaultFooter);

/**
 * Take one away.
 *
 * THE AUTOMATIC FOOTER COMES BACK when the written one goes, so a document is never left with no
 * identity on the page -- removing a footer you wrote is not a decision to print unmarked pages.
 */
function removeElement(editor: any, name: "docHeader" | "docFooter") {
    if (!editor) return;
    const at = elementAt(editor, name);
    if (at === null) return;
    const { state, view } = editor;
    const node = name === "docHeader" ? state.doc.firstChild : state.doc.lastChild;
    view.dispatch(state.tr.delete(at, at + node.nodeSize));
    view.focus();
}

export const removeHeader = (editor: any) => removeElement(editor, "docHeader");
export const removeFooter = (editor: any) => removeElement(editor, "docFooter");

/** Put a field where the caret is. Legal anywhere -- a title block wants the revision too. */
export function insertField(editor: any, name: DocumentFieldName) {
    editor?.chain().focus().insertContent({ type: "docField", attrs: { field: name } }).run();
}

/**
 * Page setup: where the running header and footer sit, and whether page one is different.
 *
 * STORED ON THE DOCUMENT NODE, as attributes. It belongs to the document rather than to this
 * browser -- the rendition has to lay the PDF out the same way -- and the doc node's attributes
 * ride along in the body that is saved and the body that is rendered, with no new column, no new
 * endpoint and no second place for a document's settings to live.
 *
 * THE ONE THING IT DOES NOT DO is reach another editor live: y-prosemirror syncs the document's
 * CONTENT, and the doc node's own attributes are not part of that. Two people with the document
 * open will not see each other change the margins until a reload. Page setup is changed once in a
 * document's life and the body is the thing that must never diverge, so this is the right side to
 * be wrong on -- and it is written down here rather than discovered later.
 */
export const DEFAULT_PAGE_SETUP = {
    headerMargin: 0.5,                 // inches from the top of the sheet
    footerMargin: 0.5,                 // inches from the bottom
    differentFirstPage: false,
    // LETTER, PORTRAIT, AN INCH ALL ROUND -- what every procedure in this system was written
    // against. These defaults are not a preference; they are the geometry the ruler, the
    // pagination preview, the tab stops and the renderer have all agreed on until now, and a
    // document that never opens the dialog must lay out exactly as it did before.
    orientation: "portrait",
    marginTop: 1,
    marginBottom: 1,
    marginLeft: 1,
    marginRight: 1,
    // ONE COLUMN, which is what every procedure written before this was. Two or three flow the
    // text down one and on into the next, and on to the following page -- the renderer does it
    // with a frame per column and the browser with CSS multi-column, which are the same layout
    // arrived at twice. See columns() in pageGeometry.ts.
    columns: 1,
};

export type PageSetup = typeof DEFAULT_PAGE_SETUP;

const inches = (value: any, fallback: number) => {
    const parsed = parseFloat(value);
    // A MARGIN BIGGER THAN THE SHEET IS NOT A MARGIN. Clamped rather than refused: a typo in a
    // number field should leave you with a page, not an error dialog.
    return Number.isFinite(parsed) ? Math.min(4, Math.max(0, parsed)) : fallback;
};

/** 1, 2 or 3. Clamped rather than refused, like a margin: a stored value nobody recognises should
 *  leave you with a readable page, and one column is what every document had before this existed. */
const columnCount = (value: any) => {
    const parsed = parseInt(value, 10);
    return parsed === 2 || parsed === 3 ? parsed : 1;
};

export const PAGE_SETUP_ATTRIBUTES = {
    orientation: {
        default: DEFAULT_PAGE_SETUP.orientation,
        parseHTML: (element: HTMLElement) =>
            element.getAttribute("data-orientation") === "landscape" ? "landscape" : "portrait",
        renderHTML: (attrs: any) => ({ "data-orientation": attrs.orientation }),
    },
    ...Object.fromEntries((["marginTop", "marginBottom", "marginLeft", "marginRight"] as const)
        .map(name => [name, {
            default: (DEFAULT_PAGE_SETUP as any)[name],
            parseHTML: (element: HTMLElement) =>
                inches(element.getAttribute(`data-${name.toLowerCase()}`),
                       (DEFAULT_PAGE_SETUP as any)[name]),
            renderHTML: (attrs: any) => ({ [`data-${name.toLowerCase()}`]: attrs[name] }),
        }])),
    headerMargin: {
        default: DEFAULT_PAGE_SETUP.headerMargin,
        parseHTML: (element: HTMLElement) => inches(element.getAttribute("data-header-margin"),
                                                   DEFAULT_PAGE_SETUP.headerMargin),
        renderHTML: (attrs: any) => ({ "data-header-margin": attrs.headerMargin }),
    },
    footerMargin: {
        default: DEFAULT_PAGE_SETUP.footerMargin,
        parseHTML: (element: HTMLElement) => inches(element.getAttribute("data-footer-margin"),
                                                   DEFAULT_PAGE_SETUP.footerMargin),
        renderHTML: (attrs: any) => ({ "data-footer-margin": attrs.footerMargin }),
    },
    // HOW MANY COLUMNS THE TEXT FLOWS DOWN. A document property, not a view setting: it changes
    // what the signed PDF looks like, so it travels with the document and anybody opening it sees
    // the same layout.
    columns: {
        default: DEFAULT_PAGE_SETUP.columns,
        parseHTML: (element: HTMLElement) => columnCount(element.getAttribute("data-columns")),
        renderHTML: (attrs: any) => ({ "data-columns": attrs.columns }),
    },
    differentFirstPage: {
        default: DEFAULT_PAGE_SETUP.differentFirstPage,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-different-first") === "true",
        renderHTML: (attrs: any) => ({ "data-different-first": attrs.differentFirstPage }),
    },
    // NUMBERED HEADINGS, OFF UNLESS SOMEBODY TURNS THEM ON. Numbering every existing procedure the
    // day this shipped would change what all of them print, with nothing in any record saying why.
    // A document-level attribute rather than a setting, because it is a property of this procedure
    // and travels with it -- see outline.ts for how the numbers are worked out, and
    // document_rendition.py for the mirror of that rule.
    outlineNumbering: {
        default: false,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-outline-numbering") === "true",
        renderHTML: (attrs: any) => ({ "data-outline-numbering": attrs.outlineNumbering }),
    },
    // HOW THE FOOTNOTES ARE NUMBERED: straight through, or from 1 again at each section. A
    // property of THIS procedure, like the heading numbering beside it -- a house that numbers per
    // section and one that numbers straight through are both ordinary, and which it is travels
    // with the document rather than with whoever opens it. See footnotes.ts and _footnotes in
    // document_rendition.py for the rule, which is mirrored.
    footnoteNumbering: {
        default: "continuous",
        parseHTML: (element: HTMLElement) => {
            const held = element.getAttribute("data-footnote-numbering");
            return held === "section" || held === "page" ? held : "continuous";
        },
        renderHTML: (attrs: any) => ({ "data-footnote-numbering": attrs.footnoteNumbering }),
    },
    // NAMED PARAGRAPH STYLES, on the DOCUMENT for the same reason page setup is: a controlled
    // procedure has to render the same a year later, and an appearance that came from a stylesheet
    // somebody else could edit would let a released revision change its look with no revision, no
    // approval and nothing in its trail. Stored as one object rather than as a field per style, so
    // adding a property to a style is not a migration. See namedStyles.ts, and the mirror of the
    // same rule in document_rendition.py.
    paragraphStyles: {
        default: null,
        parseHTML: (element: HTMLElement) => {
            try { return JSON.parse(element.getAttribute("data-paragraph-styles") || "null"); }
            catch { return null; }
        },
        renderHTML: (attrs: any) => (attrs.paragraphStyles
            ? { "data-paragraph-styles": JSON.stringify(attrs.paragraphStyles) } : {}),
    },
};

/**
 * Change one named style, or put them all back.
 *
 * THE WHOLE SHEET IS REWRITTEN each time, because it is one document attribute. `DocAttrStep` is
 * the only thing that edits the document node's own attributes -- setNodeMarkup needs a position
 * and the document has none.
 */
export function setParagraphStyles(editor: any, next: any) {
    if (!editor) return;
    const { state, view } = editor;
    const tr = state.tr;
    tr.step(new DocAttrStep("paragraphStyles", next));
    view.dispatch(tr);
}

export function pageSetup(editor: any): PageSetup {
    const attrs = editor?.state?.doc?.attrs ?? {};
    return {
        headerMargin: inches(attrs.headerMargin, DEFAULT_PAGE_SETUP.headerMargin),
        footerMargin: inches(attrs.footerMargin, DEFAULT_PAGE_SETUP.footerMargin),
        differentFirstPage: !!attrs.differentFirstPage,
        orientation: attrs.orientation === "landscape" ? "landscape" : "portrait",
        marginTop: inches(attrs.marginTop, DEFAULT_PAGE_SETUP.marginTop),
        marginBottom: inches(attrs.marginBottom, DEFAULT_PAGE_SETUP.marginBottom),
        marginLeft: inches(attrs.marginLeft, DEFAULT_PAGE_SETUP.marginLeft),
        marginRight: inches(attrs.marginRight, DEFAULT_PAGE_SETUP.marginRight),
        columns: columnCount(attrs.columns),
    };
}

/**
 * Change it.
 *
 * DocAttrStep, which is the only thing that edits the document node's own attributes -- a
 * setNodeMarkup needs a position and the document has none.
 */
export function setPageSetup(editor: any, next: Partial<PageSetup>) {
    if (!editor) return;
    const { state, view } = editor;
    const tr = state.tr;
    for (const [name, value] of Object.entries(next)) {
        if (state.doc.attrs[name] !== value) tr.step(new DocAttrStep(name, value));
    }
    if (tr.steps.length) view.dispatch(tr);
}
